/**
 * @file 양식 목록 → 폴더 트리.
 *
 * 19종을 한 줄씩 늘어놓으면 «지출 결의서 (…)» 가 네 번 반복돼 이름의 앞부분이 전부 같아진다.
 * 실제로 고르는 기준은 괄호 안인데 그게 제일 끝에 있어 눈이 매번 문장을 끝까지 읽어야 한다.
 * 그래서 같은 앞부분끼리 폴더로 묶고 괄호 안만 항목으로 남긴다.
 *
 * <h3>묶는 방법 두 가지</h3>
 * <ol>
 *   <li><b>이름에서 뽑기</b> — «지출 결의서 (행사 – 보험 가입 건)» → 폴더 «지출 결의서» + 항목 «행사 – 보험 가입 건».
 *       관리자가 새로 추가한 양식도 이름만 같은 규칙으로 지으면 자동으로 제자리에 들어간다.</li>
 *   <li><b>코드로 지정</b> — «휴가신청서 · 대체휴무 사용 신청서 · 휴가 복구 신청서» 처럼 이름이 안 겹치는데
 *       뜻으로는 한 묶음인 것들. 이건 규칙으로 못 뽑으므로 아래 표에 적는다.</li>
 * </ol>
 *
 * 폴더는 <b>2개 이상</b> 모일 때만 만든다. 혼자 있는 양식을 폴더에 넣으면 한 번 더 눌러야 보이기만 하고
 * 얻는 게 없다.
 */

/** 어느 폴더에도 안 들어간 양식을 모으는 자리. */
const MISC_FOLDER = "일반";

/**
 * 폴더 이름 → 색.
 *
 * 최상위 그룹은 노란색 하나로 두고, 하위 폴더만 뜻에 따라 색을 나눈다. 색을 나누는 층이 둘이면
 * 어느 색이 «분류» 를 뜻하는지 알 수 없어진다.
 */
export function folderColor(name, depth) {
    if (depth === 0) return "#f59e0b";                 // 최상위 — 노랑
    if (name.startsWith("휴가"))   return "#22c55e";    // 초록
    if (name.startsWith("근무"))   return "#8b5cf6";    // 보라
    if (name.startsWith("법인카드")) return "#f97316";   // 주황
    if (name.startsWith("지출"))   return "#3b82f6";    // 파랑
    if (name === MISC_FOLDER || name === "기타") return "#94a3b8";
    return "#64748b";
}

/**
 * 이름으로는 못 묶이지만 뜻으로는 한 묶음인 것들. 여기 없는 코드는 이름 규칙만 탄다.
 *
 * <p>법인카드 셋은 이름이 «지출 결의서» «지출 품의서» «법인카드 사용내역서» 로 제각각이라
 * 이름 규칙으로는 흩어진다. 실제로 찾을 때는 «법인카드 건» 하나로 떠올리므로 여기서 묶는다.
 * 개인 카드는 법인카드가 아니라 «지출 결의서» 에 그대로 둔다.
 */
const SUBGROUP_BY_CODE = {
    EXP_CARD_FIXED:     "법인카드",
    EXP_CARD_ESTIMATE:  "법인카드",
    EXP_CARD_STATEMENT: "법인카드",

    GEN_LEAVE_REQUEST: "휴가",
    GEN_COMPDAY_USE:   "휴가",
    GEN_LEAVE_RESTORE: "휴가",

    GEN_EXTRA_WORK:    "근무",
    GEN_REMOTE_WORK:   "근무",
    GEN_HOLIDAY_WORK:  "근무",
    GEN_BUSINESS_TRIP: "근무",
};

/** «지출 결의서 (행사 – 보험 가입 건)» → { base: "지출 결의서", leaf: "행사 – 보험 가입 건" } */
function splitByParen(name) {
    const m = /^(.+?)\s*[(（]\s*(.+?)\s*[)）]$/.exec(name || "");
    if (!m) return null;
    return { base: m[1].trim(), leaf: m[2].trim() };
}

/**
 * 그룹 목록 → 폴더 트리.
 *
 * @param groups [{ groupCode, groupName, forms: [{code, name, ...}] }]
 * @returns [{ groupCode, groupName, nodes: [ {kind:"folder", name, forms:[{form, label}]}
 *                                          | {kind:"form", form, label} ] }]
 */
export function buildFormTree(groups) {
    return (groups || []).map((g) => {
        const forms = g.forms || [];

        // 1) 각 양식이 어느 폴더로 갈지 정한다.
        const planned = forms.map((f) => {
            const byCode = SUBGROUP_BY_CODE[f.code];
            if (byCode) return { form: f, folder: byCode, label: f.name };
            const split = splitByParen(f.name);
            if (split) return { form: f, folder: split.base, label: split.leaf };
            return { form: f, folder: null, label: f.name };
        });

        // 2) 혼자인 폴더는 없던 일로 — 항목 이름은 전체 이름으로 되돌린다.
        const count = {};
        planned.forEach((p) => { if (p.folder) count[p.folder] = (count[p.folder] || 0) + 1; });

        // 3) 원래 순서를 지키면서 폴더는 처음 나온 자리에 놓는다.
        //    폴더에 못 들어간 것들은 마지막 «일반» 폴더로 모은다 — 최상위 바로 아래 낱개로 걸린
        //    양식 하나가 폴더들 사이에 끼면 목록의 층이 어긋나 보인다.
        const nodes = [];
        const folderAt = new Map();
        const leftover = [];
        planned.forEach((p) => {
            if (!p.folder || count[p.folder] < 2) {
                leftover.push({ form: p.form, label: p.form.name });
                return;
            }
            if (!folderAt.has(p.folder)) {
                const node = { kind: "folder", name: p.folder, forms: [] };
                folderAt.set(p.folder, node);
                nodes.push(node);
            }
            folderAt.get(p.folder).forms.push({ form: p.form, label: p.label });
        });

        if (leftover.length > 0) {
            // 부모가 이미 «일반» 이면 «일반 > 일반» 이 되므로 그때만 «기타» 로 부른다.
            const name = g.groupName === MISC_FOLDER ? "기타" : MISC_FOLDER;
            nodes.push({ kind: "folder", name, forms: leftover });
        }

        return { groupCode: g.groupCode, groupName: g.groupName, nodes };
    }).filter((g) => g.nodes.length > 0);
}

/** 관리자 목록({id, name, group, …})을 상신 목록과 같은 모양으로 맞춘다. */
export function groupsFromAdminList(forms) {
    const map = new Map();
    (forms || []).forEach((f) => {
        if (!map.has(f.group)) map.set(f.group, { groupCode: f.group, groupName: f.group, forms: [] });
        map.get(f.group).forms.push(f);
    });
    return Array.from(map.values());
}
